// yogaPoseLibrary.ts
// Curated static library of approved yoga poses keyed by dosha and ailment.
// AI generates therapeutic rationale only — pose SELECTION is deterministic.
// Consultation signals layer on top of static defaults.

export interface CuratedPose {
  name: string;
  sanskritName?: string;
  phase: 'foundation' | 'activation' | 'pranayama' | 'nature';
  imagePath: string;           // expected path in /public/images/yoga/
  practiceDescription: string;
  timing?: 'morning' | 'afternoon' | 'evening' | 'any';
  difficulty: 'beginner' | 'intermediate';
}

export interface YogaProtocolTemplate {
  foundation: CuratedPose;
  activation: CuratedPose;
  pranayama: CuratedPose;
  nature: CuratedPose;
}

/**
 * Signals extracted from the preceding consultation response.
 * Used to override the static library defaults with content the AI
 * explicitly recommended during the consultation.
 */
export interface YogaSignals {
  recommendedPranayama: string | null;     // POSE_LIBRARY key
  foundationOverride: CuratedPose | null;
  activationOverride: CuratedPose | null;
  natureOverride: string | null;           // reserved for future use
  mentionedPoses: string[];                // human-readable names for prompt context
  durationGuidance: string[];
  frequencyGuidance: string[];
}

// ─── MASTER POSE DEFINITIONS ───────────────────────────────────────────────

export const POSE_LIBRARY: Record<string, CuratedPose> = {

  // ── FOUNDATION POSES ──────────────────────────────────────────────────────

  'balasana': {
    name: "Balasana (Child's Pose)",
    sanskritName: "Balasana",
    phase: 'foundation',
    imagePath: '/images/yoga/balasana.jpg',
    practiceDescription: "Kneel on the floor, sit back on your heels, and fold forward resting your forehead on the mat. Extend arms forward or rest them alongside your body. Breathe deeply into your back. Hold for 1-3 minutes.",
    difficulty: 'beginner'
  },

  'sukhasana': {
    name: "Sukhasana (Easy Pose)",
    sanskritName: "Sukhasana",
    phase: 'foundation',
    imagePath: '/images/yoga/sukhasana.jpg',
    practiceDescription: "Sit comfortably cross-legged on the floor or a cushion. Rest hands on knees, palms facing up or down. Lengthen your spine and relax your shoulders. Close your eyes and breathe naturally for 2-5 minutes.",
    difficulty: 'beginner'
  },

  'tadasana': {
    name: "Tadasana (Mountain Pose)",
    sanskritName: "Tadasana",
    phase: 'foundation',
    imagePath: '/images/yoga/tadasana.jpg',
    practiceDescription: "Stand with feet together or hip-width apart. Ground all four corners of your feet. Lengthen your spine, relax your shoulders, and let arms hang naturally. Breathe steadily for 1-2 minutes feeling rooted and tall.",
    difficulty: 'beginner'
  },

  'virabhadrasana-1': {
    name: "Virabhadrasana I (Warrior I)",
    sanskritName: "Virabhadrasana I",
    phase: 'foundation',
    imagePath: '/images/yoga/virabhadrasana-1.jpg',
    practiceDescription: "Step one foot forward into a lunge, front knee over ankle. Back foot turns out 45 degrees. Raise both arms overhead, palms facing each other. Look forward and breathe steadily. Hold for 30-60 seconds each side.",
    difficulty: 'beginner'
  },

  'virabhadrasana-2': {
    name: "Virabhadrasana II (Warrior II)",
    sanskritName: "Virabhadrasana II",
    phase: 'foundation',
    imagePath: '/images/yoga/virabhadrasana-2.jpg',
    practiceDescription: "Stand with feet wide apart. Turn right foot out 90 degrees. Bend right knee over right ankle. Extend arms out to the sides at shoulder height, gaze over right hand. Hold for 30-60 seconds each side.",
    difficulty: 'beginner'
  },

  'viparita-karani': {
    name: "Viparita Karani (Legs Up the Wall)",
    sanskritName: "Viparita Karani",
    phase: 'foundation',
    imagePath: '/images/yoga/viparita-karani.jpg',
    practiceDescription: "Lie on your back close to a wall. Swing legs up the wall so your body forms an L-shape. Rest arms by your sides, palms up. Close eyes and breathe slowly. Hold for 5-10 minutes. Ideal before sleep.",
    difficulty: 'beginner'
  },

  'pavanamuktasana': {
    name: "Pavanamuktasana (Wind Relieving Pose)",
    sanskritName: "Pavanamuktasana",
    phase: 'foundation',
    imagePath: '/images/yoga/pavanamuktasana.jpg',
    practiceDescription: "Lie on your back. Inhale, then on exhale draw both knees to your chest. Wrap arms around shins and gently rock side to side. Press lower back into the mat. Hold for 1-2 minutes breathing deeply.",
    difficulty: 'beginner'
  },

  'sukhasana-neck-rolls': {
    name: "Sukhasana with Neck Rolls",
    phase: 'foundation',
    imagePath: '/images/yoga/sukhasana-with-neck-rolls.jpg',
    practiceDescription: "Sit in Easy Pose. Drop right ear toward right shoulder, hold 3 breaths. Roll chin to chest, hold 3 breaths. Drop left ear to left shoulder, hold 3 breaths. Repeat slowly 3 times each side. Never roll head fully back.",
    difficulty: 'beginner'
  },

  'trikonasana': {
    name: "Trikonasana (Triangle Pose)",
    sanskritName: "Trikonasana",
    phase: 'foundation',
    imagePath: '/images/yoga/trikonasana.jpg',
    practiceDescription: "Stand with feet wide apart. Turn right foot out 90 degrees. Extend arms out, then reach right hand down to shin or floor. Left arm reaches straight up. Look up at left hand. Hold for 30-60 seconds each side.",
    difficulty: 'beginner'
  },
  
  'virasana': {
    name: "Virasana (Hero Pose)",
    sanskritName: "Virasana",
    phase: 'foundation',
    imagePath: '/images/yoga/virasana.jpg',
    practiceDescription: "Seated upright on a yoga block for support, knees together and feet spread wide outside the hips. Tops of the feet rest on the floor. Spine tall, hands rest softly on thighs. Calming and grounding.",
    difficulty: 'beginner'
  },

  'setubandhasana': {
    name: "Setu Bandhasana (Bridge Pose)",
    sanskritName: "Setu Bandhasana",
    phase: 'foundation',
    imagePath: '/images/yoga/setubandhasana.jpg',
    practiceDescription: "Lying on your back with knees bent, lift your hips high and interlace hands underneath for support. Open the chest toward the chin. Breathe steadily and hold.",
    difficulty: 'beginner'
  },

  'badhkonsasana': {
    name: "Baddha Konasana (Butterfly Pose)",
    sanskritName: "Baddha Konasana",
    phase: 'foundation',
    imagePath: '/images/yoga/badhkonsasana.jpg',
    practiceDescription: "Seated with soles of feet together, knees dropping toward the mat. Hold feet and lengthen the spine. Gazing softly forward. Peaceful and contemplative hip opener.",
    difficulty: 'beginner'
  },

  // ── ACTIVATION POSES ──────────────────────────────────────────────────────

  'cat-cow': {
    name: "Marjaryasana-Bitilasana (Cat-Cow)",
    sanskritName: "Marjaryasana-Bitilasana",
    phase: 'activation',
    imagePath: '/images/yoga/cat-cow.jpg',
    practiceDescription: "Come to hands and knees, wrists under shoulders, knees under hips. Inhale: drop belly, lift chest and tailbone (Cow). Exhale: round spine toward ceiling, tuck chin and tailbone (Cat). Flow slowly for 1-3 minutes.",
    difficulty: 'beginner'
  },

  'surya-namaskar': {
    name: "Surya Namaskar (Sun Salutation)",
    sanskritName: "Surya Namaskar",
    phase: 'activation',
    imagePath: '/images/yoga/surya-namaskar.jpg',
    practiceDescription: "Stand at the top of your mat. Flow through: forward fold, half lift, plank, lower down, upward dog or cobra, downward dog, step forward, rise up with arms overhead, return to standing. Complete 3-6 rounds at a pace that suits your energy.",
    difficulty: 'beginner'
  },

  'paschimottanasana': {
    name: "Paschimottanasana (Seated Forward Bend)",
    sanskritName: "Paschimottanasana",
    phase: 'activation',
    imagePath: '/images/yoga/paschimottanasana.jpg',
    practiceDescription: "Sit with legs extended straight in front. Inhale and lengthen your spine. Exhale and fold forward from the hips, reaching hands toward feet. Keep spine long rather than rounding. Hold for 1-3 minutes breathing deeply.",
    difficulty: 'beginner'
  },

  'gomukhasana-arms': {
    name: "Gomukhasana Arms (Cow Face Arms)",
    sanskritName: "Gomukhasana",
    phase: 'activation',
    imagePath: '/images/yoga/gomukhasana-arms.jpg',
    practiceDescription: "Sit or stand. Raise right arm overhead and bend elbow, dropping hand behind your head. Bring left arm behind your back and try to clasp hands. If hands do not meet, hold a strap or towel. Hold for 1 minute each side.",
    difficulty: 'beginner'
  },

  'dhanurasana': {
    name: "Dhanurasana (Bow Pose)",
    sanskritName: "Dhanurasana",
    phase: 'activation',
    imagePath: '/images/yoga/dhanurasana.jpg',
    practiceDescription: "Lying on your belly, arch backward and lift your chest while grasping your ankles. Pull your body into a bow shape. Gaze forward, breathe deeply into the chest. Builds strength and determination.",
    difficulty: 'beginner'
  },

  'bhujangasana': {
    name: "Bhujangasana (Cobra Pose)",
    sanskritName: "Bhujangasana",
    phase: 'activation',
    imagePath: '/images/yoga/bhujangasana.jpg',
    practiceDescription: "Lying on the belly, press palms into the mat to lift the chest and head. Keep hips grounded, gaze upwards. Opens the heart and strengthens the spine.",
    difficulty: 'beginner'
  },

  'adhomukhshvanasana': {
    name: "Adho Mukha Svanasana (Downward-Facing Dog)",
    sanskritName: "Adho Mukha Svanasana",
    phase: 'activation',
    imagePath: '/images/yoga/adhomukhshvanasana.jpg',
    practiceDescription: "From all fours, tuck toes and lift hips high into an inverted V. Press palms firmly, straighten spine, and reach heels toward the mat. Look back toward your feet.",
    difficulty: 'beginner'
  },

  // ── PRANAYAMA ──────────────────────────────────────────────────────────────

  'anuloma-viloma': {
    name: "Anuloma Viloma (Alternate Nostril Breathing)",
    sanskritName: "Anuloma Viloma",
    phase: 'pranayama',
    imagePath: '/images/yoga/anuloma-viloma.jpg',
    practiceDescription: "Sit comfortably. Use right hand: thumb closes right nostril, ring finger closes left. Inhale through left nostril (4 counts), close both, exhale through right (8 counts). Inhale right (4 counts), close both, exhale left (8 counts). This is one round. Complete 5-10 rounds.",
    timing: 'morning',
    difficulty: 'beginner'
  },

  'kapalbhati': {
    name: "Kapalbhati (Skull Shining Breath)",
    sanskritName: "Kapalbhati",
    phase: 'pranayama',
    imagePath: '/images/yoga/kapalbhati.jpg',
    practiceDescription: "Sit comfortably with spine erect. Take a deep inhale. Begin short, sharp, forceful exhales through the nose by pumping the belly inward. Inhalation happens passively. Start with 20 pumps, rest, repeat 3 rounds. Avoid if pregnant or hypertensive.",
    timing: 'morning',
    difficulty: 'beginner'
  },

  'bhramari': {
    name: "Bhramari (Humming Bee Breath)",
    sanskritName: "Bhramari",
    phase: 'pranayama',
    imagePath: '/images/yoga/bhramari.png',
    practiceDescription: "Sit quietly. Close ears with thumbs, rest fingers gently on face. Take a full inhale. On exhale make a steady humming sound like a bee for the entire exhale. Feel the vibration in your head and chest. Complete 5-7 rounds. Excellent for anxiety and sleep.",
    timing: 'evening',
    difficulty: 'beginner'
  },

  'shitali': {
    name: "Shitali (Cooling Breath)",
    sanskritName: "Shitali",
    phase: 'pranayama',
    imagePath: '/images/yoga/sheetali.jpg',
    practiceDescription: "Sit comfortably. Roll your tongue into a tube shape and extend it slightly past the lips. Inhale slowly through the rolled tongue feeling the cool air. Close mouth and exhale slowly through the nose. Complete 8-12 rounds. Best practised in afternoon or evening for Pitta.",
    timing: 'afternoon',
    difficulty: 'beginner'
  },

  'bhastrika': {
    name: "Bhastrika (Bellows Breath)",
    sanskritName: "Bhastrika",
    phase: 'pranayama',
    imagePath: '/images/yoga/bhastrika.jpg',
    practiceDescription: "Sit with spine erect. Take a deep inhale then exhale forcefully through the nose. Immediately follow with a forceful inhale. Both inhale and exhale are active and equal. Start with 10 rounds, rest, repeat 2-3 times. Warming and energising — ideal for Kapha.",
    timing: 'morning',
    difficulty: 'beginner'
  },

  // ── NATURE INTEGRATION ────────────────────────────────────────────────────

  'mindful-grounding-walk': {
    name: "Mindful Grounding Walk",
    phase: 'nature',
    imagePath: '/images/yoga/mindful-grounding-walk.jpg',
    practiceDescription: "Walk at a slow to moderate pace in a natural setting — park, garden, or any green space. Focus your full attention on the sensation of your feet making contact with the ground. Notice each step. Walk for 15-20 minutes without phone or distraction.",
    difficulty: 'beginner'
  },

  'barefoot-grounding': {
    name: "Barefoot Grounding (Bhumi Sparsha)",
    sanskritName: "Bhumi Sparsha",
    phase: 'nature',
    imagePath: '/images/yoga/barefoot-grounding.jpg',
    practiceDescription: "Remove shoes and socks. Stand or walk slowly on grass, soil, or sand. Feel the earth beneath your feet. If standing still, close eyes and breathe deeply for 5 minutes. If walking, take slow mindful steps for 10-15 minutes. Best done in morning on dew-covered grass.",
    difficulty: 'beginner'
  },

  'barefoot-dew-walking': {
    name: "Barefoot Dew Walking (Bhumi Sparsha)",
    sanskritName: "Bhumi Sparsha",
    phase: 'nature',
    imagePath: '/images/yoga/barefoot-dew-walking.jpg',
    practiceDescription: "Early morning practice. Remove shoes and walk slowly on dew-covered grass for 10-15 minutes. The cool morning dew on the soles of the feet is deeply calming for Pitta and Vata. Focus on the cooling sensation with each step.",
    timing: 'morning',
    difficulty: 'beginner'
  },

  'brisk-nature-walk': {
    name: "Brisk Nature Walk",
    phase: 'nature',
    imagePath: '/images/yoga/brisk-nature-walk.png',
    practiceDescription: "Walk at a brisk, energising pace in a natural outdoor setting. Swing arms naturally, breathe deeply through the nose. Aim for 20-30 minutes. This warming movement is ideal for Kapha types who benefit from stimulating exercise to move stagnant energy.",
    difficulty: 'beginner'
  },

  'gentle-nature-walk': {
    name: "Gentle Nature Walk",
    phase: 'nature',
    imagePath: '/images/yoga/gentle-nature-walk.jpg',
    practiceDescription: "Walk at a relaxed, unhurred pace in a park or natural setting. Keep breathing slow and nasal. Allow your gaze to be soft, taking in the natural surroundings. Walk for 15-20 minutes. Ideal for recovery, pain conditions, or high Vata states.",
    difficulty: 'beginner'
  },

  'arghyam': {
    name: "Arghyam (Morning Sun Offering)",
    sanskritName: "Arghyam",
    phase: 'nature',
    imagePath: '/images/yoga/arghyam.jpg',
    practiceDescription: "Stand facing the rising sun. Cup water in joined palms and slowly pour it in a thin stream toward the sun while mentally offering gratitude. Repeat 3-7 times. Then stand quietly for 2-3 minutes absorbing morning sunlight on face and palms. Best practised within one hour of sunrise.",
    timing: 'morning',
    difficulty: 'beginner'
  },

  'surya-darshan': {
    name: "Surya Darshan (Sun Gazing)",
    sanskritName: "Surya Darshan",
    phase: 'nature',
    imagePath: '/images/yoga/surya-darshan.jpg',
    practiceDescription: "During the first 10 minutes after sunrise or the last 10 minutes before sunset only — gaze softly at the sun for 10-30 seconds. Build gradually over weeks. Stand barefoot on earth if possible. Never gaze at the sun when it is high in the sky.",
    timing: 'morning',
    difficulty: 'beginner'
  },

  'trataka': {
    name: "Trataka (Focused Gazing)",
    sanskritName: "Trataka",
    phase: 'nature',
    imagePath: '/images/yoga/trataka.jpg',
    practiceDescription: "Place a candle flame at eye level 60-90cm away or choose a small object. Gaze steadily without blinking for as long as comfortable. When eyes water or become fatigued, close them and visualise the flame internally. Practice for 5-10 minutes. Strengthens eye muscles and concentration.",
    difficulty: 'beginner'
  },

  'evening-moon-walk': {
    name: "Evening Moon Walk",
    phase: 'nature',
    imagePath: '/images/yoga/evening-moon-walk.jpg',
    practiceDescription: "Walk slowly in the evening or night air, ideally where moonlight is visible. Keep pace gentle and unhurried. Breathe slowly and deeply. Allow the cool evening air and soft light to calm the nervous system. Walk for 15-20 minutes before bed.",
    timing: 'evening',
    difficulty: 'beginner'
  },

  'post-meal-walk': {
    name: "Post-Meal Gentle Walk (Shatapavali)",
    sanskritName: "Shatapavali",
    phase: 'nature',
    imagePath: '/images/yoga/post-meal-walk.jpg',
    practiceDescription: "Within 10-15 minutes after a meal, walk at a very gentle pace for 100 steps (Shatapavali) or about 5-10 minutes. This ancient Ayurvedic practice stimulates digestion without straining it. Keep pace very slow — this is not exercise, it is digestive support.",
    difficulty: 'beginner'
  },

  'slow-mindful-walk': {
    name: "Slow Mindful Walk",
    phase: 'nature',
    imagePath: '/images/yoga/slow-mindful-walk.jpg',
    practiceDescription: "Walk very slowly in any calm environment. With each step, consciously place the heel down first, then roll through the foot to the toes. Synchronise breath with steps — inhale for 4 steps, exhale for 4 steps. Walk for 15-20 minutes. Ideal for hypertension and joint conditions.",
    difficulty: 'beginner'
  }
};

// ─── DOSHA PROTOCOL TEMPLATES ──────────────────────────────────────────────

export const DOSHA_PROTOCOLS: Record<string, {
  morning: YogaProtocolTemplate;
  afternoon: YogaProtocolTemplate;
}> = {
  vata: {
    morning: {
      foundation: POSE_LIBRARY['balasana'],
      activation: POSE_LIBRARY['surya-namaskar'],
      pranayama: POSE_LIBRARY['anuloma-viloma'],
      nature: POSE_LIBRARY['mindful-grounding-walk']
    },
    afternoon: {
      foundation: POSE_LIBRARY['sukhasana'],
      activation: POSE_LIBRARY['cat-cow'],
      pranayama: POSE_LIBRARY['bhramari'],
      nature: POSE_LIBRARY['gentle-nature-walk']
    }
  },
  pitta: {
    morning: {
      foundation: POSE_LIBRARY['tadasana'],
      activation: POSE_LIBRARY['cat-cow'],
      pranayama: POSE_LIBRARY['anuloma-viloma'],
      nature: POSE_LIBRARY['barefoot-grounding']
    },
    afternoon: {
      foundation: POSE_LIBRARY['sukhasana'],
      activation: POSE_LIBRARY['paschimottanasana'],
      pranayama: POSE_LIBRARY['shitali'],
      nature: POSE_LIBRARY['barefoot-dew-walking']
    }
  },
  kapha: {
    morning: {
      foundation: POSE_LIBRARY['tadasana'],
      activation: POSE_LIBRARY['surya-namaskar'],
      pranayama: POSE_LIBRARY['kapalbhati'],
      nature: POSE_LIBRARY['brisk-nature-walk']
    },
    afternoon: {
      foundation: POSE_LIBRARY['virabhadrasana-1'],
      activation: POSE_LIBRARY['virabhadrasana-2'],
      pranayama: POSE_LIBRARY['bhastrika'],
      nature: POSE_LIBRARY['brisk-nature-walk']
    }
  },
  sama: {
    morning: {
      foundation: POSE_LIBRARY['sukhasana'],
      activation: POSE_LIBRARY['surya-namaskar'],
      pranayama: POSE_LIBRARY['anuloma-viloma'],
      nature: POSE_LIBRARY['mindful-grounding-walk']
    },
    afternoon: {
      foundation: POSE_LIBRARY['tadasana'],
      activation: POSE_LIBRARY['trikonasana'],
      pranayama: POSE_LIBRARY['bhramari'],
      nature: POSE_LIBRARY['gentle-nature-walk']
    }
  }
};

// ─── AILMENT PROTOCOL OVERRIDES ────────────────────────────────────────────
// These take priority over any dosha-based selection.

export const AILMENT_PROTOCOLS: Record<string, YogaProtocolTemplate> = {
  anxiety: {
    foundation: POSE_LIBRARY['balasana'],
    activation: POSE_LIBRARY['cat-cow'],
    pranayama: POSE_LIBRARY['bhramari'],
    nature: POSE_LIBRARY['mindful-grounding-walk']
  },
  stress: {
    foundation: POSE_LIBRARY['balasana'],
    activation: POSE_LIBRARY['cat-cow'],
    pranayama: POSE_LIBRARY['bhramari'],
    nature: POSE_LIBRARY['mindful-grounding-walk']
  },
  'back pain': {
    foundation: POSE_LIBRARY['balasana'],
    activation: POSE_LIBRARY['cat-cow'],
    pranayama: POSE_LIBRARY['anuloma-viloma'],
    nature: POSE_LIBRARY['gentle-nature-walk']
  },
  insomnia: {
    foundation: POSE_LIBRARY['viparita-karani'],
    activation: POSE_LIBRARY['paschimottanasana'],
    pranayama: POSE_LIBRARY['bhramari'],
    nature: POSE_LIBRARY['evening-moon-walk']
  },
  sleep: {
    foundation: POSE_LIBRARY['viparita-karani'],
    activation: POSE_LIBRARY['paschimottanasana'],
    pranayama: POSE_LIBRARY['bhramari'],
    nature: POSE_LIBRARY['evening-moon-walk']
  },
  digestion: {
    foundation: POSE_LIBRARY['pavanamuktasana'],
    activation: POSE_LIBRARY['cat-cow'],
    pranayama: POSE_LIBRARY['kapalbhati'],
    nature: POSE_LIBRARY['post-meal-walk']
  },
  digestive: {
    foundation: POSE_LIBRARY['pavanamuktasana'],
    activation: POSE_LIBRARY['cat-cow'],
    pranayama: POSE_LIBRARY['kapalbhati'],
    nature: POSE_LIBRARY['post-meal-walk']
  },
  'high blood pressure': {
    foundation: POSE_LIBRARY['sukhasana'],
    activation: POSE_LIBRARY['paschimottanasana'],
    pranayama: POSE_LIBRARY['anuloma-viloma'],
    nature: POSE_LIBRARY['slow-mindful-walk']
  },
  hypertension: {
    foundation: POSE_LIBRARY['sukhasana'],
    activation: POSE_LIBRARY['paschimottanasana'],
    pranayama: POSE_LIBRARY['anuloma-viloma'],
    nature: POSE_LIBRARY['slow-mindful-walk']
  },
  'neck tension': {
    foundation: POSE_LIBRARY['sukhasana-neck-rolls'],
    activation: POSE_LIBRARY['gomukhasana-arms'],
    pranayama: POSE_LIBRARY['anuloma-viloma'],
    nature: POSE_LIBRARY['gentle-nature-walk']
  },
  'neck pain': {
    foundation: POSE_LIBRARY['sukhasana-neck-rolls'],
    activation: POSE_LIBRARY['gomukhasana-arms'],
    pranayama: POSE_LIBRARY['anuloma-viloma'],
    nature: POSE_LIBRARY['gentle-nature-walk']
  },
  fatigue: {
    foundation: POSE_LIBRARY['virabhadrasana-1'],
    activation: POSE_LIBRARY['surya-namaskar'],
    pranayama: POSE_LIBRARY['kapalbhati'],
    nature: POSE_LIBRARY['brisk-nature-walk']
  },
  depression: {
    foundation: POSE_LIBRARY['virabhadrasana-2'],
    activation: POSE_LIBRARY['surya-namaskar'],
    pranayama: POSE_LIBRARY['kapalbhati'],
    nature: POSE_LIBRARY['surya-darshan']
  },
  'joint pain': {
    foundation: POSE_LIBRARY['tadasana'],
    activation: POSE_LIBRARY['cat-cow'],
    pranayama: POSE_LIBRARY['anuloma-viloma'],
    nature: POSE_LIBRARY['slow-mindful-walk']
  },
  arthritis: {
    foundation: POSE_LIBRARY['tadasana'],
    activation: POSE_LIBRARY['cat-cow'],
    pranayama: POSE_LIBRARY['anuloma-viloma'],
    nature: POSE_LIBRARY['slow-mindful-walk']
  },
  headache: {
    foundation: POSE_LIBRARY['balasana'],
    activation: POSE_LIBRARY['paschimottanasana'],
    pranayama: POSE_LIBRARY['shitali'],
    nature: POSE_LIBRARY['gentle-nature-walk']
  },
  migraine: {
    foundation: POSE_LIBRARY['balasana'],
    activation: POSE_LIBRARY['paschimottanasana'],
    pranayama: POSE_LIBRARY['shitali'],
    nature: POSE_LIBRARY['gentle-nature-walk']
  },
  immunity: {
    foundation: POSE_LIBRARY['tadasana'],
    activation: POSE_LIBRARY['surya-namaskar'],
    pranayama: POSE_LIBRARY['kapalbhati'],
    nature: POSE_LIBRARY['brisk-nature-walk']
  },
  morning: {
    foundation: POSE_LIBRARY['tadasana'],
    activation: POSE_LIBRARY['surya-namaskar'],
    pranayama: POSE_LIBRARY['anuloma-viloma'],
    nature: POSE_LIBRARY['arghyam']
  },
  'eye strain': {
    foundation: POSE_LIBRARY['sukhasana'],
    activation: POSE_LIBRARY['paschimottanasana'],
    pranayama: POSE_LIBRARY['bhramari'],
    nature: POSE_LIBRARY['trataka']
  },
  vision: {
    foundation: POSE_LIBRARY['sukhasana'],
    activation: POSE_LIBRARY['paschimottanasana'],
    pranayama: POSE_LIBRARY['bhramari'],
    nature: POSE_LIBRARY['trataka']
  }
};

// ─── CONSULTATION-DERIVED DYNAMIC POSES ────────────────────────────────────
// These are not in POSE_LIBRARY (too specific) but can be recommended by the AI
// during a consultation. They are constructed on-the-fly by extractYogaSignals.

export const DYNAMIC_POSE_BUILDERS: Record<string, () => CuratedPose> = {
  'neck and shoulder release': () => ({
    name: 'Gentle Neck and Shoulder Release',
    phase: 'foundation',
    imagePath: '/images/yoga/neck-shoulder-release.jpg',
    practiceDescription: 'Slowly roll your head from side to side, then bring chin to chest and lift back. Follow with shoulder rolls forward and backward. Repeat 3-5 times each direction to release overnight tension.',
    difficulty: 'beginner'
  }),
  'neck roll': () => ({
    name: 'Gentle Neck and Shoulder Release',
    phase: 'foundation',
    imagePath: '/images/yoga/neck-shoulder-release.jpg',
    practiceDescription: 'Slowly roll your head from side to side, then bring chin to chest and lift back. Follow with shoulder rolls forward and backward. Repeat 3-5 times each direction.',
    difficulty: 'beginner'
  }),
  'seated spinal twist': () => ({
    name: 'Seated Spinal Twist (Ardha Matsyendrasana)',
    sanskritName: 'Ardha Matsyendrasana',
    phase: 'activation',
    imagePath: '/images/yoga/ardha-matsyendrasana.jpg',
    practiceDescription: 'Sit upright in a chair or on the floor. Gently twist your torso to the right, placing left hand on right knee and right hand behind you for support. Hold for 3 breaths, then repeat on the left side. Awakens the spine and improves circulation.',
    difficulty: 'beginner'
  }),
  'spinal twist': () => ({
    name: 'Seated Spinal Twist (Ardha Matsyendrasana)',
    sanskritName: 'Ardha Matsyendrasana',
    phase: 'activation',
    imagePath: '/images/yoga/ardha-matsyendrasana.jpg',
    practiceDescription: 'Sit upright, gently twist torso to right, left hand on right knee, right hand behind for support. Hold 3 breaths each side.',
    difficulty: 'beginner'
  }),
  'chest opening': () => ({
    name: 'Chest Opening Stretch',
    phase: 'activation',
    imagePath: '/images/yoga/chest-opening-stretch.jpg',
    practiceDescription: 'Stand or sit tall. Interlace fingers behind your back, straighten arms and gently lift them while expanding your chest toward the ceiling. Hold for 5-8 breaths. Opens the heart space and encourages deeper breathing.',
    difficulty: 'beginner'
  }),
  'chest stretch': () => ({
    name: 'Chest Opening Stretch',
    phase: 'activation',
    imagePath: '/images/yoga/chest-opening-stretch.jpg',
    practiceDescription: 'Interlace fingers behind your back, straighten arms and gently lift while expanding your chest. Hold for 5-8 breaths.',
    difficulty: 'beginner'
  }),
  'leg and ankle': () => ({
    name: 'Leg and Ankle Awakening',
    phase: 'activation',
    imagePath: '/images/yoga/leg-ankle-awakening.jpg',
    practiceDescription: 'Seated or lying down, extend one leg forward and flex the foot, pointing toes up then down. Rotate the ankle clockwise then counterclockwise 5 times each. Repeat on the other leg. Improves circulation in the lower body.',
    difficulty: 'beginner'
  }),
  'ankle rotation': () => ({
    name: 'Leg and Ankle Awakening',
    phase: 'activation',
    imagePath: '/images/yoga/leg-ankle-awakening.jpg',
    practiceDescription: 'Extend one leg, flex the foot, rotate ankle 5 times each direction. Repeat on the other side.',
    difficulty: 'beginner'
  }),
  'shoulder roll': () => ({
    name: 'Shoulder Roll and Release',
    phase: 'foundation',
    imagePath: '/images/yoga/gomukhasana-arms.jpg',
    practiceDescription: 'Sit or stand tall. Roll both shoulders slowly forward in a large circle 5 times, then reverse for 5 backward rolls. Follow with shoulder shrugs — lift both shoulders to ears, hold 2 seconds, release. Repeat 5 times.',
    difficulty: 'beginner'
  }),
  'wrist rotation': () => ({
    name: 'Wrist and Finger Awakening',
    phase: 'activation',
    imagePath: '/images/yoga/leg-ankle-awakening.jpg',
    practiceDescription: 'Extend both arms in front. Rotate wrists clockwise 5 times then counterclockwise 5 times. Spread and fan fingers wide, hold 3 seconds, release. Repeat 3 times. Excellent for desk workers and joint stiffness.',
    difficulty: 'beginner'
  }),
  'standing forward bend': () => ({
    name: 'Standing Forward Bend (Uttanasana)',
    sanskritName: 'Uttanasana',
    phase: 'activation',
    imagePath: '/images/yoga/uttanasana.jpg',
    practiceDescription: 'Stand with feet hip-width apart. Inhale and lengthen your spine. Exhale and fold forward from the hips, letting your head and arms hang heavy. Bend knees slightly if needed. Hold for 5-8 breaths, then roll slowly back up to standing.',
    difficulty: 'beginner'
  }),
};

// ─── LOOKUP FUNCTION ───────────────────────────────────────────────────────

/**
 * Internal: returns the static-library base protocol before consultation overrides.
 */
function getBaseProtocol(
  ailment: string,
  dosha: string,
  timeOfDay: 'morning' | 'afternoon' | 'evening'
): YogaProtocolTemplate {
  const ailmentLower = ailment.toLowerCase();

  // 1. Ailment-specific override (highest priority)
  for (const [key, protocol] of Object.entries(AILMENT_PROTOCOLS)) {
    if (ailmentLower.includes(key)) {
      console.log(`[YogaLibrary] Ailment match: "${ailment}" → "${key}"`);
      return protocol;
    }
  }

  // 2. Dosha-based fallback
  const doshaLower = dosha.toLowerCase();
  const doshaKey = doshaLower.includes('pitta') ? 'pitta'
    : doshaLower.includes('vata') ? 'vata'
    : doshaLower.includes('kapha') ? 'kapha'
    : 'sama';

  const timingKey: 'morning' | 'afternoon' = timeOfDay === 'morning' ? 'morning' : 'afternoon';
  const protocol = DOSHA_PROTOCOLS[doshaKey]?.[timingKey] ?? DOSHA_PROTOCOLS['sama']['morning'];

  console.log(`[YogaLibrary] Dosha fallback: "${dosha}" → "${doshaKey}" ${timingKey}`);
  return protocol;
}

/**
 * Returns the curated YogaProtocolTemplate for a given ailment and dosha,
 * then applies any consultation-derived signal overrides on top.
 *
 * Priority (highest → lowest):
 *   1. Consultation signal overrides  (explicit AI recommendation from chat)
 *   2. Ailment-specific library entry (AILMENT_PROTOCOLS)
 *   3. Dosha-based protocol           (DOSHA_PROTOCOLS)
 */
export function getYogaProtocol(
  ailment: string,
  dosha: string,
  timeOfDay: 'morning' | 'afternoon' | 'evening' = 'morning',
  consultationSignals?: YogaSignals
): YogaProtocolTemplate {
  // Get the static base
  const base = getBaseProtocol(ailment, dosha, timeOfDay);

  // No signals — return base unchanged
  if (!consultationSignals) return base;

  const result: YogaProtocolTemplate = { ...base };

  // Apply foundation override
  if (consultationSignals.foundationOverride) {
    console.log(
      `[YogaLibrary] Foundation override: "${base.foundation.name}"` +
      ` → "${consultationSignals.foundationOverride.name}"`
    );
    result.foundation = consultationSignals.foundationOverride;
  }

  // Apply activation override
  if (consultationSignals.activationOverride) {
    console.log(
      `[YogaLibrary] Activation override: "${base.activation.name}"` +
      ` → "${consultationSignals.activationOverride.name}"`
    );
    result.activation = consultationSignals.activationOverride;
  }

  // Apply pranayama override (maps POSE_LIBRARY key → CuratedPose)
  if (consultationSignals.recommendedPranayama) {
    const overridePose = POSE_LIBRARY[consultationSignals.recommendedPranayama];
    if (overridePose) {
      console.log(
        `[YogaLibrary] Pranayama override: "${base.pranayama.name}"` +
        ` → "${overridePose.name}"`
      );
      result.pranayama = overridePose;
    }
  }

  return result;
}

// ─── CONSULTATION SIGNAL EXTRACTOR ────────────────────────────────────────
// Parses the consultation response text to extract explicit pose/pranayama
// recommendations. Returns a YogaSignals object used by getYogaProtocol().

/** Pranayama keyword → POSE_LIBRARY key mapping */
const PRANAYAMA_DETECTION_MAP: Record<string, string> = {
  'sheetali': 'shitali',
  'shitali': 'shitali',
  'sitali': 'shitali',
  'bhramari': 'bhramari',
  'kapalbhati': 'kapalbhati',
  'kapalabhati': 'kapalbhati',
  'anuloma viloma': 'anuloma-viloma',
  'nadi shodhana': 'anuloma-viloma',
  'alternate nostril': 'anuloma-viloma',
  'bhastrika': 'bhastrika',
  'ujjayi': 'ujjayi',
  'chandra bhedana': 'chandra-bhedana',
};

/**
 * Extracts yoga pose and pranayama signals from a consultation response.
 * Matches POSE_LIBRARY names first (static), then DYNAMIC_POSE_BUILDERS.
 * First match per phase wins — does NOT stack multiple overrides.
 */
export function extractYogaSignalsFromConsultation(consultationText: string): YogaSignals {
  const text = consultationText.toLowerCase();

  // ── 1. Pranayama detection ────────────────────────────────────────────────
  let recommendedPranayama: string | null = null;
  for (const [keyword, poseId] of Object.entries(PRANAYAMA_DETECTION_MAP)) {
    if (text.includes(keyword)) {
      recommendedPranayama = poseId;
      console.log(`[YogaAid] Pranayama extracted: "${keyword}" → "${poseId}"`);
      break;
    }
  }

  // ── 2. Named POSE_LIBRARY pose detection ──────────────────────────────────
  // Check static library poses by their human-readable names
  const STATIC_POSE_KEYWORDS: Array<{ keywords: string[]; libraryKey: string }> = [
    { keywords: ['surya namaskar', 'sun salutation'], libraryKey: 'surya-namaskar' },
    { keywords: ['cat cow', 'cat-cow', 'marjaryasana', 'bitilasana'], libraryKey: 'cat-cow' },
    { keywords: ["child's pose", 'balasana', 'child pose'], libraryKey: 'balasana' },
    { keywords: ['tadasana', 'mountain pose'], libraryKey: 'tadasana' },
    { keywords: ['trikonasana', 'triangle pose'], libraryKey: 'trikonasana' },
    { keywords: ['paschimottanasana', 'seated forward bend', 'seated forward fold'], libraryKey: 'paschimottanasana' },
    { keywords: ['virabhadrasana ii', 'warrior ii', 'warrior 2'], libraryKey: 'virabhadrasana-2' },
    { keywords: ['virabhadrasana i', 'warrior i', 'warrior 1', 'virabhadrasana', 'warrior'], libraryKey: 'virabhadrasana-1' },
    { keywords: ['pavanamuktasana', 'pawanmuktasana', 'wind relieving'], libraryKey: 'pavanamuktasana' },
    { keywords: ['viparita karani', 'legs up the wall'], libraryKey: 'viparita-karani' },
    { keywords: ['gomukhasana', 'cow face'], libraryKey: 'gomukhasana-arms' },
    { keywords: ['sukhasana with neck rolls', 'neck rolls'], libraryKey: 'sukhasana-neck-rolls' },
    { keywords: ['sukhasana', 'easy pose'], libraryKey: 'sukhasana' },
    { keywords: ['virasana', 'hero pose'], libraryKey: 'virasana' },
    { keywords: ['setubandhasana', 'setu bandhasana', 'bridge pose'], libraryKey: 'setubandhasana' },
    { keywords: ['baddha konasana', 'badhkonsasana', 'butterfly pose'], libraryKey: 'badhkonsasana' },
    { keywords: ['dhanurasana', 'bow pose'], libraryKey: 'dhanurasana' },
    { keywords: ['bhujangasana', 'cobra pose'], libraryKey: 'bhujangasana' },
    { keywords: ['adho mukha svanasana', 'adho mukh shvanasana', 'adhomukhshvanasana', 'downward facing dog', 'downward-facing dog'], libraryKey: 'adhomukhshvanasana' },
  ];

  // ── 3. Build detected pose list ───────────────────────────────────────────
  const mentionedPoses: string[] = [];
  let foundationOverride: CuratedPose | null = null;
  let activationOverride: CuratedPose | null = null;

  // Helper to detect if a pose is declared unsuitable
  const isPoseDeclaredUnsuitable = (txt: string, keywords: string[]): boolean => {
    const sentences = txt.split(/[.!?\n]/);
    const negativePatterns = [
      /not\s+(suitable|suited|recommended|safe|good|beneficial)/i,
      /avoid/i,
      /contraindicated/i,
      /should\s+not/i,
      /unsafe/i,
      /aggravate/i,
      /harmful/i
    ];
    
    for (const sentence of sentences) {
      const hasKeyword = keywords.some(k => sentence.toLowerCase().includes(k));
      if (hasKeyword) {
        if (negativePatterns.some(pattern => pattern.test(sentence))) {
          return true;
        }
      }
    }
    return false;
  };

  // Check static keyword → library key first
  for (const { keywords, libraryKey } of STATIC_POSE_KEYWORDS) {
    if (keywords.some(k => text.includes(k))) {
      if (isPoseDeclaredUnsuitable(text, keywords)) {
        console.log(`[YogaAid] Static pose "${libraryKey}" is declared unsuitable in consultation. Skipping override.`);
        continue;
      }
      const pose = POSE_LIBRARY[libraryKey];
      if (!pose) continue;
      mentionedPoses.push(pose.name);
      console.log(`[YogaAid] Static pose extracted: "${libraryKey}" → "${pose.name}"`);
      if (pose.phase === 'foundation' && !foundationOverride) foundationOverride = pose;
      else if (pose.phase === 'activation' && !activationOverride) activationOverride = pose;
    }
  }

  // Check dynamic pose builders (consultation-specific phrases)
  for (const [keyword, builder] of Object.entries(DYNAMIC_POSE_BUILDERS)) {
    if (text.includes(keyword)) {
      if (isPoseDeclaredUnsuitable(text, [keyword])) {
        console.log(`[YogaAid] Dynamic pose for "${keyword}" is declared unsuitable in consultation. Skipping override.`);
        continue;
      }
      const pose = builder();
      // Avoid duplicate by name
      if (!mentionedPoses.includes(pose.name)) {
        mentionedPoses.push(pose.name);
        console.log(`[YogaAid] Dynamic pose extracted: "${keyword}" → "${pose.name}"`);
        if (pose.phase === 'foundation' && !foundationOverride) foundationOverride = pose;
        else if (pose.phase === 'activation' && !activationOverride) activationOverride = pose;
      }
    }
  }

  // ── 4. Duration and frequency extraction ─────────────────────────────────
  const durationGuidance = (
    consultationText.match(/\d+[\s-]*minutes?[^\.]*\./gi) ?? []
  ).map(m => m.trim());

  const frequencyGuidance = (
    consultationText.match(/\d+[\s-]*times?[^\.]*\./gi) ?? []
  ).map(m => m.trim());

  return {
    recommendedPranayama,
    foundationOverride,
    activationOverride,
    natureOverride: null,
    mentionedPoses,
    durationGuidance,
    frequencyGuidance,
  };
}

/**
 * Resolves a CuratedPose to the YogaPose shape expected by the UI.
 * The AI fills in benefit, instructions, contraindications, and doshaAdaptation.
 * This function provides the shell with pre-filled structural fields.
 */
export function curatedPoseToYogaPoseShell(pose: CuratedPose, phaseLabel: string): object {
  return {
    phase: phaseLabel,
    pose_name: pose.name,
    type: pose.phase === 'pranayama' ? 'Pranayama' : pose.phase === 'nature' ? 'Exercise' : 'Asana',
    benefit: '',
    instructions: pose.practiceDescription,
    contraindications: '',
    doshaNote: '',
    _imagePath: pose.imagePath,
  };
}
